import { normalizePageResponse } from "../lib/pagination";
import { imsGet, imsPost, imsPut, imsPatch, hrmsGet, crmGet } from "../api/api";
import sessionDB from "../utils/sessionDB";

/**
 * Inventory Location / Warehouse Master service — IMS inventory module.
 * Endpoints: /saas/ims/inventory-locations/*
 * Warehouse types: GET /saas/hrms/org/warehouse-types
 */

const SEARCH = "IMS03S";
const ADD = "IMS03A";
const UPDATE = "IMS03U";
const LOCATION_STOCK_LEVELS_SAVE = "IMS09A";
const LOCATION_STOCK_LEVELS_SEARCH = "IMS09S";
const LOCATION_STOCK_LEVELS_UPDATE = "IMS09U";
const HRMS_ORG = "HR14A";

const DEFAULT_PAGE = { page: 0, size: 50, sortBy: "warehouseName", sortDir: "ASC" };

/** Share in-flight identical GETs (helps React Strict Mode double-mount). */
const inflightRequests = new Map();

function dedupeAsync(key, factory) {
  const existing = inflightRequests.get(key);
  if (existing) return existing;
  const promise = Promise.resolve()
    .then(factory)
    .finally(() => {
      if (inflightRequests.get(key) === promise) inflightRequests.delete(key);
    });
  inflightRequests.set(key, promise);
  return promise;
}

function unwrapData(response) {
  if (response == null) return response;
  if (response.data !== undefined) {
    if (
      response.data &&
      typeof response.data === "object" &&
      !Array.isArray(response.data) &&
      response.data.data !== undefined
    ) {
      return response.data.data;
    }
    return response.data;
  }
  return response;
}

function unwrapPage(response, params) {
  return normalizePageResponse(response, params ? { ...DEFAULT_PAGE, ...params } : { paginate: false });
}
function unwrapItem(response) {
  const data = unwrapData(response);
  if (Array.isArray(data)) return data[0] || null;
  return data && typeof data === "object" ? data : null;
}

function extractCount(response) {
  const data = unwrapData(response);
  if (typeof data === "number") return data;
  if (typeof data?.count === "number") return data.count;
  if (typeof data?.total === "number") return data.total;
  if (typeof data?.totalElements === "number") return data.totalElements;
  const n = Number(data);
  return Number.isFinite(n) ? n : 0;
}

function firstDefined(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function emptyToNull(value) {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  return s === "" ? null : s;
}

function toNumberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function resolveCreatedBy() {
  const empId = sessionDB.getEmpId?.();
  if (empId !== undefined && empId !== null && empId !== "") {
    const asNum = Number(empId);
    return Number.isNaN(asNum) ? String(empId) : asNum;
  }
  const emp = sessionDB.getEmpObj?.() || {};
  const fallback =
    emp.emp_id ?? emp.empId ?? emp.employeeId ?? emp.employee_id ?? emp.id ?? null;
  if (fallback == null || fallback === "") return null;
  const asNum = Number(fallback);
  return Number.isNaN(asNum) ? String(fallback) : asNum;
}

/** UI status → API body enum. */
export function toApiWarehouseStatus(uiStatus) {
  const lower = String(uiStatus || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (lower === "inactive") return "Inactive";
  return "Active";
}

/** Query-param form used by search/count endpoints. */
export function toSearchWarehouseStatus(uiStatus) {
  return toApiWarehouseStatus(uiStatus);
}

export function toUiWarehouseStatus(apiStatus, isActive) {
  if (isActive === false || isActive === "false" || isActive === 0) return "Inactive";
  const lower = String(apiStatus || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (lower === "inactive" || lower === "disabled" || lower === "deactivated") return "Inactive";
  return "Active";
}

export function toApiPrimaryHub(uiValue) {
  if (uiValue === true || uiValue === "Yes" || uiValue === "YES" || uiValue === "true" || uiValue === 1) {
    return true;
  }
  return false;
}

export function toUiPrimaryHub(apiValue) {
  if (
    apiValue === true ||
    apiValue === "Yes" ||
    apiValue === "YES" ||
    apiValue === "true" ||
    apiValue === 1 ||
    String(apiValue || "").toLowerCase() === "yes"
  ) {
    return "Yes";
  }
  return "No";
}

function normalizeDocuments(raw) {
  if (!raw) return [];
  if (typeof raw === "string") {
    const name = raw.trim();
    return name ? [{ id: name, name, size: "", uploadDate: "" }] : [];
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((doc, index) => {
      if (doc == null) return null;
      if (typeof doc === "string") {
        const name = doc.trim();
        return name ? { id: `doc-${index}-${name}`, name, size: "", uploadDate: "" } : null;
      }
      const name = firstDefined(
        doc.documentName,
        doc.document_name,
        doc.fileName,
        doc.file_name,
        doc.name,
        doc.url
      );
      if (!name) return null;
      return {
        id: String(firstDefined(doc.documentId, doc.document_id, doc.id, `doc-${index}`)),
        name: String(name),
        size: firstDefined(doc.size, doc.fileSize, doc.file_size) || "",
        uploadDate: firstDefined(doc.uploadDate, doc.upload_date, doc.createdAt, doc.created_at) || "",
      };
    })
    .filter(Boolean);
}

function mapDocumentsToApi(documents) {
  if (!Array.isArray(documents) || documents.length === 0) return null;
  const names = documents
    .map((f) => (typeof f === "string" ? f : f?.name || f?.fileName || f?.documentName))
    .map((name) => (name != null ? String(name).trim() : ""))
    .filter(Boolean);
  return names.length > 0 ? names : null;
}

function mapStockItems(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      const id = firstDefined(item.stockItemId, item.stock_item_id, item.productId, item.product_id, item.id, index);
      return {
        id: String(id),
        productName: firstDefined(item.productName, item.product_name, item.name) || "—",
        sku: firstDefined(item.sku, item.productSku, item.product_sku, item.skuCode) || "—",
        total: toNumberOrZero(firstDefined(item.total, item.qtyOnHand, item.qty_on_hand, item.quantity)),
        reserved: toNumberOrZero(firstDefined(item.reserved, item.qtyReserved, item.qty_reserved)),
        defective: toNumberOrZero(firstDefined(item.defective, item.qtyDefective, item.qty_defective)),
        maintenanceReserve: toNumberOrZero(
          firstDefined(item.maintenanceReserve, item.maintenance_reserve, item.qtyMaintenance)
        ),
        available: toNumberOrZero(firstDefined(item.available, item.qtyAvailable, item.qty_available)),
        stockValue: toNumberOrZero(firstDefined(item.stockValue, item.stock_value, item.value)),
      };
    })
    .filter(Boolean);
}

/** Cache of warehouseTypeId → name learned from API responses. */
const warehouseTypeRegistry = new Map();

function rememberWarehouseType(id, name) {
  if (id == null || id === "" || !name) return;
  const label = String(name).trim();
  if (!label || /^\d+$/.test(label) || label === "[object Object]") return;
  warehouseTypeRegistry.set(String(id), label);
}

function nameFromWarehouseTypeValue(value) {
  if (value == null || value === "") return undefined;
  if (typeof value === "object") {
    return firstDefined(
      value.warehouseTypeName,
      value.warehouse_type_name,
      value.typeName,
      value.type_name,
      value.name,
      value.label
    );
  }
  const label = String(value).trim();
  if (!label || /^\d+$/.test(label) || label === "[object Object]") return undefined;
  return label;
}

function idFromWarehouseTypeValue(value) {
  if (value == null || value === "") return undefined;
  if (typeof value === "object") {
    return firstDefined(
      value.warehouseTypeId,
      value.warehouse_type_id,
      value.typeId,
      value.type_id,
      value.id
    );
  }
  const asId = String(value).trim();
  return /^\d+$/.test(asId) ? asId : undefined;
}

/** Presentable warehouse type label — "Project_Site" → "Project Site". */
export function formatWarehouseTypeLabel(name) {
  if (name == null || name === "") return "";
  return String(name)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** True when warehouse type is Project Site (API may use Project_Site / Project Site). */
export function isProjectSiteWarehouseType(typeName) {
  return formatWarehouseTypeLabel(typeName).toLowerCase() === "project site";
}

export function resolveWarehouseTypeLabel(typeId, typeName) {
  const label = nameFromWarehouseTypeValue(typeName);
  if (label) {
    rememberWarehouseType(typeId, label);
    return formatWarehouseTypeLabel(label);
  }
  if (typeId == null || typeId === "") return "";
  return formatWarehouseTypeLabel(warehouseTypeRegistry.get(String(typeId)) || "");
}

/** Resolve a display name from the warehouse record and/or loaded type options. */
export function displayWarehouseType(warehouse, typeOptions = []) {
  if (!warehouse) return "";
  const id = warehouse.warehouseTypeId;
  const fromOptions = (typeOptions || []).find((t) => String(t.id) === String(id));
  if (fromOptions?.name) return formatWarehouseTypeLabel(fromOptions.name);
  return resolveWarehouseTypeLabel(id, warehouse.type);
}

/**
 * Map API warehouse entity → UI shape used by list / detail / form pages.
 */
export function mapApiWarehouseToUi(api) {
  if (!api || typeof api !== "object") return null;

  const id = firstDefined(api.warehouseId, api.warehouse_id, api.locationId, api.location_id, api.id);
  const nestedType = firstDefined(api.warehouseType, api.warehouse_type);
  const warehouseTypeId = firstDefined(
    api.warehouseTypeId,
    api.warehouse_type_id,
    api.typeId,
    api.type_id,
    idFromWarehouseTypeValue(nestedType),
    idFromWarehouseTypeValue(api.type)
  );
  const warehouseTypeName = firstDefined(
    nameFromWarehouseTypeValue(api.warehouseTypeName),
    nameFromWarehouseTypeValue(api.warehouse_type_name),
    nameFromWarehouseTypeValue(nestedType),
    nameFromWarehouseTypeValue(api.typeName),
    nameFromWarehouseTypeValue(api.type_name),
    nameFromWarehouseTypeValue(api.type)
  );
  const typeLabel = resolveWarehouseTypeLabel(warehouseTypeId, warehouseTypeName);

  const statusRaw = firstDefined(api.warehouseStatus, api.warehouse_status, api.status);
  const isActive = firstDefined(api.isActive, api.is_active, api.active);

  const managerId = firstDefined(
    api.warehouseManagerId,
    api.warehouse_manager_id,
    api.managerId,
    api.manager_id
  );
  const managerName = firstDefined(
    api.warehouseManagerName,
    api.warehouse_manager_name,
    api.managerName,
    api.manager_name,
    api.manager
  );

  const customerId = firstDefined(api.customerId, api.customer_id);
  const customerName = firstDefined(
    api.customerName,
    api.customer_name,
    api.companyName,
    api.company_name
  );

  const primaryHubRaw = firstDefined(
    api.isPrimaryHub,
    api.is_primary_hub,
    api.primaryHub,
    api.primary_hub,
    api.hubWarehouse,
    api.hub_warehouse
  );

  const fifo = firstDefined(
    api.fifoFefoSupport,
    api.fifo_fefo_support,
    api.dispatchStrategy,
    api.dispatch_strategy,
    api.fifoFefo
  );

  const documents = normalizeDocuments(
    firstDefined(api.documents, api.documentList, api.document_list, api.attachments)
  );

  const stockItems = mapStockItems(
    firstDefined(api.stockItems, api.stock_items, api.inventoryItems, api.inventory_items, api.products)
  );

  return {
    id: id != null ? String(id) : undefined,
    warehouseId: id != null ? String(id) : undefined,
    name: firstDefined(api.warehouseName, api.warehouse_name, api.locationName, api.name) || "",
    code: firstDefined(api.warehouseCode, api.warehouse_code, api.code) || "",
    type: typeLabel || "",
    warehouseTypeId: warehouseTypeId != null ? String(warehouseTypeId) : "",
    status: toUiWarehouseStatus(statusRaw, isActive),
    primaryHub: toUiPrimaryHub(primaryHubRaw),
    addressLine1: firstDefined(api.addressLine1, api.address_line_1, api.address1) || "",
    addressLine2: firstDefined(api.addressLine2, api.address_line_2, api.address2) || "",
    city: firstDefined(api.city, api.warehouseCity, api.warehouse_city) || "",
    state: firstDefined(api.state, api.warehouseState, api.warehouse_state) || "",
    pincode: firstDefined(api.pincode, api.pinCode, api.pin_code, api.postalCode, api.postal_code) || "",
    manager: managerName || (managerId != null ? `Employee #${managerId}` : ""),
    managerId: managerId != null ? String(managerId) : "",
    customerId: customerId != null ? String(customerId) : "",
    customerName: customerName || (customerId != null ? `Customer #${customerId}` : ""),
    fifoFefoSupport: fifo || "FIFO",
    documents,
    skusHeld: toNumberOrZero(firstDefined(api.skusHeld, api.skus_held, api.skuCount, api.sku_count, stockItems.length)),
    totalUnits: toNumberOrZero(firstDefined(api.totalUnits, api.total_units, api.qtyOnHand, api.qty_on_hand)),
    reservedUnits: toNumberOrZero(
      firstDefined(api.reservedUnits, api.reserved_units, api.qtyReserved, api.qty_reserved)
    ),
    stockValue: toNumberOrZero(firstDefined(api.stockValue, api.stock_value, api.totalStockValue)),
    stockItems,
    raw: api,
  };
}

/**
 * Map UI form fields → API create/update body.
 */
export function mapFormToApiPayload(form) {
  const createdByRaw =
    form.createdBy != null && form.createdBy !== "" ? form.createdBy : resolveCreatedBy();
  const createdBy = toNumberOrNull(createdByRaw) ?? createdByRaw;

  const warehouseTypeId =
    toNumberOrNull(form.warehouseTypeId) ??
    toNumberOrNull(form.typeId) ??
    (form.type && /^\d+$/.test(String(form.type).trim()) ? Number(form.type) : null);

  const managerId =
    toNumberOrNull(form.managerId) ??
    toNumberOrNull(form.warehouseManagerId) ??
    toNumberOrNull(form.manager);

  const includeCustomer = isProjectSiteWarehouseType(form.type || form.warehouseTypeName);
  const customerId = includeCustomer
    ? toNumberOrNull(form.customerId) ?? toNumberOrNull(form.customer_id)
    : null;

  return {
    warehouseName: emptyToNull(form.name || form.warehouseName),
    warehouseCode: emptyToNull(form.code || form.warehouseCode),
    warehouseTypeId,
    status: toApiWarehouseStatus(form.status || form.warehouseStatus || "Active"),
    isPrimaryHub: toApiPrimaryHub(form.primaryHub ?? form.isPrimaryHub),
    addressLine1: emptyToNull(form.addressLine1),
    addressLine2: emptyToNull(form.addressLine2),
    city: emptyToNull(form.city),
    state: emptyToNull(form.state),
    pincode: emptyToNull(form.pincode || form.pinCode),
    warehouseManagerId: managerId,
    customerId,
    fifoFefoSupport: emptyToNull(form.fifoFefoSupport) || "FIFO",
    documents: mapDocumentsToApi(form.documents),
    createdBy,
  };
}

function buildPageQuery({ page, size, sortBy, sortDir } = {}) {
  const p = {
    page: page ?? DEFAULT_PAGE.page,
    size: size ?? DEFAULT_PAGE.size,
    sortBy: sortBy ?? DEFAULT_PAGE.sortBy,
    sortDir: sortDir ?? DEFAULT_PAGE.sortDir,
  };
  return `page=${p.page}&size=${p.size}&sortBy=${encodeURIComponent(p.sortBy)}&sortDir=${encodeURIComponent(p.sortDir)}`;
}

function mapPageToUi(page) {
  const content = (page.content || []).map(mapApiWarehouseToUi).filter(Boolean);
  return { ...page, content, warehouses: content };
}

/** GET /inventory-locations/search */
export async function searchWarehouses(params = {}) {
  const query = buildPageQuery({ ...params, sortBy: params.sortBy || "warehouseId" });
  return dedupeAsync(`searchWarehouses:${query}`, async () => {
    const response = await imsGet(`/inventory-locations/search?${query}`, SEARCH);
    return mapPageToUi(unwrapPage(response, params));
  });
}

/** GET /inventory-locations/search/type/{warehouseTypeId} */
export async function searchWarehousesByType(warehouseTypeId, params = {}) {
  const query = buildPageQuery({ ...params, sortBy: params.sortBy || "warehouseName" });
  const response = await imsGet(
    `/inventory-locations/search/type/${encodeURIComponent(warehouseTypeId)}?${query}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/** GET /inventory-locations/search/status */
export async function searchWarehousesByStatus(status, params = {}) {
  const apiStatus = toSearchWarehouseStatus(status);
  const query = buildPageQuery({ ...params, sortBy: params.sortBy || "warehouseName" });
  const response = await imsGet(
    `/inventory-locations/search/status?status=${encodeURIComponent(apiStatus)}&${query}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/** GET /inventory-locations/search/primary-hub */
export async function searchPrimaryHubWarehouses(params = {}) {
  const query = buildPageQuery({ ...params, sortBy: params.sortBy || "warehouseName" });
  const response = await imsGet(`/inventory-locations/search/primary-hub?${query}`, SEARCH);
  return mapPageToUi(unwrapPage(response, params));
}

/** GET /inventory-locations/search/name */
export async function searchWarehousesByName(warehouseName, params = {}) {
  const query = buildPageQuery({ ...params, sortBy: params.sortBy || "warehouseName" });
  const response = await imsGet(
    `/inventory-locations/search/name?warehouseName=${encodeURIComponent(warehouseName)}&${query}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/** GET /inventory-locations/search/manager/{warehouseManagerId} */
export async function searchWarehousesByManager(warehouseManagerId, params = {}) {
  const query = buildPageQuery({ ...params, sortBy: params.sortBy || "warehouseName" });
  const response = await imsGet(
    `/inventory-locations/search/manager/${encodeURIComponent(warehouseManagerId)}?${query}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/** GET /inventory-locations/search/city */
export async function searchWarehousesByCity(city, params = {}) {
  const query = buildPageQuery({ ...params, sortBy: params.sortBy || "warehouseName" });
  const response = await imsGet(
    `/inventory-locations/search/city?city=${encodeURIComponent(city)}&${query}`,
    SEARCH
  );
  return mapPageToUi(unwrapPage(response, params));
}

/** GET /inventory-locations/{warehouseId} */
export async function fetchWarehouseById(warehouseId) {
  return dedupeAsync(`warehouseById:${warehouseId}`, async () => {
    const response = await imsGet(`/inventory-locations/${encodeURIComponent(warehouseId)}`, SEARCH);
    return mapApiWarehouseToUi(unwrapItem(response));
  });
}

/** GET /inventory-locations/count */
export async function fetchWarehouseCount() {
  const response = await imsGet("/inventory-locations/count", SEARCH);
  return extractCount(response);
}

/** GET /inventory-locations/count/status */
export async function fetchWarehouseCountByStatus(status) {
  const apiStatus = String(toSearchWarehouseStatus(status) || "").toLowerCase();
  const response = await imsGet(
    `/inventory-locations/count/status?status=${encodeURIComponent(apiStatus)}`,
    SEARCH
  );
  return extractCount(response);
}

function mergeWarehouseUiWithRequest(mapped, body, form) {
  if (!mapped) return mapped;
  const fromBody = mapApiWarehouseToUi({
    ...body,
    warehouseId: mapped.warehouseId ?? mapped.id,
    warehouseTypeName: form?.type || mapped.type,
    warehouseManagerName: form?.manager || mapped.manager,
  }) || {};

  return {
    ...fromBody,
    ...mapped,
    name: mapped.name || fromBody.name || form?.name || "",
    code: mapped.code || fromBody.code || form?.code || "",
    type: mapped.type || form?.type || fromBody.type || "",
    warehouseTypeId: mapped.warehouseTypeId || fromBody.warehouseTypeId || form?.warehouseTypeId || "",
    status: mapped.status || fromBody.status || form?.status || "Active",
    primaryHub: mapped.primaryHub || fromBody.primaryHub || form?.primaryHub || "No",
    addressLine1: mapped.addressLine1 || fromBody.addressLine1 || form?.addressLine1 || "",
    addressLine2: mapped.addressLine2 || fromBody.addressLine2 || form?.addressLine2 || "",
    city: mapped.city || fromBody.city || form?.city || "",
    state: mapped.state || fromBody.state || form?.state || "",
    pincode: mapped.pincode || fromBody.pincode || form?.pincode || "",
    manager: mapped.manager || form?.manager || fromBody.manager || "",
    managerId: mapped.managerId || form?.managerId || fromBody.managerId || "",
    customerId: mapped.customerId || form?.customerId || fromBody.customerId || "",
    customerName: mapped.customerName || form?.customerName || fromBody.customerName || "",
    fifoFefoSupport: mapped.fifoFefoSupport || form?.fifoFefoSupport || fromBody.fifoFefoSupport || "FIFO",
    documents:
      mapped.documents && mapped.documents.length > 0
        ? mapped.documents
        : Array.isArray(form?.documents)
          ? form.documents
          : fromBody.documents || [],
  };
}

/** POST /inventory-locations/add */
export async function createWarehouse(form) {
  const body = mapFormToApiPayload(form);
  const response = await imsPost("/inventory-locations/add", body, ADD);
  const item = unwrapItem(response);
  const mapped = mapApiWarehouseToUi(item);
  if (mapped?.id) return mergeWarehouseUiWithRequest(mapped, body, form);

  const fallbackId = firstDefined(item?.warehouseId, item?.warehouse_id, item?.id);
  return (
    mergeWarehouseUiWithRequest(mapApiWarehouseToUi({ ...body, warehouseId: fallbackId }), body, form) || {
      id: fallbackId != null ? String(fallbackId) : undefined,
      ...form,
    }
  );
}

/** PUT /inventory-locations/update/{warehouseId} */
export async function updateWarehouse(warehouseId, form) {
  const body = mapFormToApiPayload(form);
  const response = await imsPut(`/inventory-locations/update/${warehouseId}`, body, UPDATE);
  const mapped = mapApiWarehouseToUi(unwrapItem(response));
  return mapped?.id
    ? mergeWarehouseUiWithRequest(mapped, body, form)
    : mergeWarehouseUiWithRequest(mapApiWarehouseToUi({ ...body, warehouseId }), body, form) || {
        id: String(warehouseId),
        ...form,
      };
}

/**
 * GET /saas/hrms/org/warehouse-types?tenantId=…
 * Returns [{ id, name, isActive }] for dropdowns / filters.
 */
export async function fetchWarehouseTypes() {
  const tenantId = sessionDB.getTenantId?.() || "";
  return dedupeAsync(`warehouseTypes:${tenantId}`, async () => {
    const response = await hrmsGet(
      `/org/warehouse-types?tenantId=${encodeURIComponent(tenantId)}`,
      HRMS_ORG
    );
    const data = unwrapData(response);
    const candidates = [
      data,
      data?.content,
      data?.warehouseTypes,
      data?.warehouse_types,
      data?.types,
      data?.records,
      data?.items,
    ];
    const list = candidates.find((v) => Array.isArray(v)) || [];

    return list
      .map((item) => {
        if (item == null) return null;
        if (typeof item === "string") {
          return { id: item, name: item, isActive: true };
        }
        const id = firstDefined(
          item.warehouseTypeId,
          item.warehouse_type_id,
          item.typeId,
          item.type_id,
          item.id
        );
        const name = firstDefined(
          item.warehouseTypeName,
          item.warehouse_type_name,
          item.typeName,
          item.type_name,
          item.name,
          item.label
        );
        if (id == null && !name) return null;
        const isActiveRaw = firstDefined(item.isActive, item.is_active, item.active, item.status);
        const isActive =
          isActiveRaw === undefined ||
          isActiveRaw === null ||
          isActiveRaw === true ||
          isActiveRaw === "true" ||
          isActiveRaw === 1 ||
          String(isActiveRaw).toLowerCase() === "active";
        if (id != null && name) rememberWarehouseType(id, name);
        return {
          id: id != null ? String(id) : String(name),
          name: name || `Type #${id}`,
          isActive,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  });
}

/**
 * Manager options from HRMS GET /emp-info/search.
 * Shape: [{ id, name, code, designation, department }]
 */
export async function fetchHrmsEmployees({ size = 100 } = {}) {
  return dedupeAsync(`hrmsEmployees:${size}`, async () => {
    const response = await hrmsGet(
      `/emp-info/search?page=0&size=${size}&sortBy=empId&sortDir=ASC`,
      "HR01S"
    );
    const data = response?.data ?? response;
    const list = data?.content ?? (Array.isArray(data) ? data : []);

    return list
      .map((emp) => {
        const id = firstDefined(emp.empId, emp.employeeId, emp.emp_id, emp.id);
        if (id == null) return null;
        const name =
          firstDefined(
            emp.fullName,
            [emp.firstName, emp.lastName].filter(Boolean).join(" "),
            [emp.first_Name, emp.last_Name].filter(Boolean).join(" "),
            emp.name,
            emp.email
          ) || `Employee #${id}`;
        return {
          id: String(id),
          name: String(name),
          code: String(firstDefined(emp.empCode, emp.emp_code, emp.employeeCode, emp.code, id)),
          designation: firstDefined(emp.designation, emp.designationName, emp.jobTitle, emp.role) || "",
          department: firstDefined(emp.department, emp.departmentName, emp.deptName) || "",
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  });
}

/**
 * GET /saas/hrms/current-employment/search/canonicalRoleName?searchTerm=Inventory_Manager — HR02S
 * Shape: [{ id, name, code, designation }]
 */
export async function fetchInventoryManagers({
  page = 0,
  size = 10,
  sortBy = "first_name",
  sortDir = "ASC",
} = {}) {
  const query = `searchTerm=Inventory_Manager&page=${page}&size=${size}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}`;
  return dedupeAsync(`inventoryManagers:${query}`, async () => {
    const response = await hrmsGet(
      `/current-employment/search/canonicalRoleName?${query}`,
      "HR02S"
    );
    const data = response?.data ?? response;
    const list = data?.content ?? (Array.isArray(data) ? data : []);

    return list
      .map((emp) => {
        if (emp == null || typeof emp !== "object") return null;
        const id = firstDefined(emp.empId, emp.emp_id, emp.employeeId, emp.employee_id, emp.id);
        if (id == null) return null;
        const firstName = firstDefined(emp.firstName, emp.first_Name, emp.first_name) || "";
        const lastName = firstDefined(emp.lastName, emp.last_Name, emp.last_name) || "";
        const name =
          firstDefined(
            emp.fullName,
            emp.employeeName,
            emp.name,
            [firstName, lastName].filter(Boolean).join(" "),
            emp.email
          ) || `Employee #${id}`;
        return {
          id: String(id),
          name: String(name).trim(),
          code: String(firstDefined(emp.empCode, emp.emp_code, emp.employeeCode, emp.code, id)),
          designation:
            firstDefined(
              emp.designation,
              emp.designationName,
              emp.canonicalRoleName,
              emp.roleName,
              emp.jobTitle,
              emp.role
            ) || "Inventory Manager",
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  });
}

/**
 * GET /saas/crm/customer-info/search — CRM01S
 * Shape: [{ id, name }]
 */
export async function fetchWarehouseCustomers({
  page = 0,
  size = 10,
  sortBy = "customerId",
  sortDir = "ASC",
} = {}) {
  const query = `page=${page}&sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}&size=${size}`;
  return dedupeAsync(`warehouseCustomers:${query}`, async () => {
    const response = await crmGet(`/customer-info/search?${query}`, "CRM01S");
    const data = response?.data ?? response;
    const list = data?.content ?? (Array.isArray(data) ? data : []);

    return list
      .map((row) => {
        if (row == null || typeof row !== "object") return null;
        const id = firstDefined(row.customerId, row.customer_id, row.id);
        if (id == null) return null;
        const name =
          firstDefined(row.companyName, row.company_name, row.customerName, row.customer_name, row.name) ||
          `Customer #${id}`;
        return { id: String(id), name: String(name).trim() };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  });
}

/** Lookups needed by Add/Edit Warehouse. */
export async function fetchWarehouseFormLookups({ size = 10 } = {}) {
  const [typesResult, employeesResult, customersResult] = await Promise.allSettled([
    fetchWarehouseTypes(),
    fetchInventoryManagers({ size }),
    fetchWarehouseCustomers({ size }),
  ]);
  return {
    warehouseTypes:
      typesResult.status === "fulfilled" ? (typesResult.value || []).filter((t) => t.isActive !== false) : [],
    employees: employeesResult.status === "fulfilled" ? employeesResult.value || [] : [],
    customers: customersResult.status === "fulfilled" ? customersResult.value || [] : [],
  };
}

/**
 * High-level list loader for WarehouseList.
 * Uses the most specific search endpoint for server-side filtering.
 */
export async function loadWarehouseCatalog({
  searchQuery = "",
  filterTypeId = "All",
  filterStatus = "All",
  page = 0,
  size = 50,
} = {}) {
  const params = { page, size, sortBy: "warehouseName", sortDir: "ASC" };
  const q = searchQuery.trim();

  // Seed warehouseTypeId → name before mapping list rows so Type is not a dash
  // when the location search payload only returns an id.
  try {
    await fetchWarehouseTypes();
  } catch {
    /* still return warehouses; names may resolve later from the types list */
  }

  let result;
  if (q) {
    result = await searchWarehousesByName(q, params);
  } else if (filterTypeId !== "All" && filterTypeId != null && filterTypeId !== "") {
    result = await searchWarehousesByType(filterTypeId, params);
  } else if (filterStatus !== "All") {
    result = await searchWarehousesByStatus(filterStatus, params);
  } else {
    result = await searchWarehouses({ ...params, sortBy: "warehouseId" });
  }

  let warehouses = result.content || [];

  // Combine filters client-side when search or type was the primary endpoint.
  if (q || (filterTypeId !== "All" && filterStatus !== "All")) {
    if (filterTypeId !== "All" && filterTypeId != null && filterTypeId !== "") {
      warehouses = warehouses.filter((w) => String(w.warehouseTypeId) === String(filterTypeId));
    }
    if (filterStatus !== "All") {
      warehouses = warehouses.filter((w) => w.status === filterStatus);
    }
  }

  return { ...result, content: warehouses, warehouses };
}

export function formatCurrency(val) {
  if (typeof val !== "number" || Number.isNaN(val)) return "₹0";
  return "₹" + val.toLocaleString("en-IN");
}

function stockQtyOrZero(value) {
  if (value === "" || value === null || value === undefined) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Service safety qty is stored as serviceSafetyStock, with minimumStockLevel kept in sync. */
function applyServiceSafetyQty(target, value) {
  const qty = stockQtyOrZero(value);
  target.serviceSafetyStock = qty;
  target.minimumStockLevel = qty;
}

function applySalesSafetyQty(target, value) {
  target.salesSafetyStock = stockQtyOrZero(value);
}

/**
 * POST /saas/ims/location-stock-levels/save — IMS09A
 * Empty / blank quantities are sent as 0.
 * Service Safety Stock → serviceSafetyStock (and minimumStockLevel).
 * Sales Safety Stock → salesSafetyStock.
 */
export async function saveLocationStockLevels(warehouseId, items = []) {
  const body = {
    warehouseId: Number(warehouseId),
    items: (items || []).map((item) => {
      const entry = { productId: Number(item.productId) };
      // API requires both fields; blank / omitted quantities are sent as 0.
      const serviceValue = Object.prototype.hasOwnProperty.call(item, "serviceSafetyStock")
        ? item.serviceSafetyStock
        : Object.prototype.hasOwnProperty.call(item, "minimumStockLevel")
          ? item.minimumStockLevel
          : 0;
      applyServiceSafetyQty(entry, serviceValue);
      applySalesSafetyQty(
        entry,
        Object.prototype.hasOwnProperty.call(item, "salesSafetyStock")
          ? item.salesSafetyStock
          : 0
      );
      return entry;
    }),
  };
  return imsPost("/location-stock-levels/save", body, LOCATION_STOCK_LEVELS_SAVE);
}

/**
 * PATCH /saas/ims/location-stock-levels/update/warehouse/{warehouseId}/product/{productId} — IMS09U
 * Sends only the fields present on `fields` (serviceSafetyStock and/or salesSafetyStock).
 */
export async function updateLocationStockLevel(warehouseId, productId, fields = {}) {
  const body = {};
  if (
    Object.prototype.hasOwnProperty.call(fields, "serviceSafetyStock") ||
    Object.prototype.hasOwnProperty.call(fields, "minimumStockLevel")
  ) {
    applyServiceSafetyQty(
      body,
      Object.prototype.hasOwnProperty.call(fields, "serviceSafetyStock")
        ? fields.serviceSafetyStock
        : fields.minimumStockLevel
    );
  }
  if (Object.prototype.hasOwnProperty.call(fields, "salesSafetyStock")) {
    applySalesSafetyQty(body, fields.salesSafetyStock);
  }

  return imsPatch(
    `/location-stock-levels/update/warehouse/${encodeURIComponent(warehouseId)}/product/${encodeURIComponent(productId)}`,
    body,
    LOCATION_STOCK_LEVELS_UPDATE
  );
}

function normalizeStockLevelValue(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function changedStockField(enabled, currentLevels, initialLevels, rowKey) {
  if (!enabled) return undefined;
  const current = normalizeStockLevelValue(currentLevels[rowKey]);
  const initial = normalizeStockLevelValue(initialLevels[rowKey]);
  const hadInitial = Object.prototype.hasOwnProperty.call(initialLevels, rowKey);
  if (current === initial) return undefined;
  if (!hadInitial && current == null) return undefined;
  return current == null ? 0 : current;
}

/**
 * Persist only changed stock levels:
 * - previously configured products → PATCH IMS09U (changed fields only)
 * - newly set products → POST IMS09A (batch)
 * Disabled product flags are omitted so those quantities are not overwritten.
 */
export async function persistLocationStockLevelChanges(
  warehouseId,
  {
    products = [],
    currentServiceLevels = {},
    initialServiceLevels = {},
    currentSalesLevels = {},
    initialSalesLevels = {},
    configuredProductIds = {},
  } = {}
) {
  const toCreate = [];
  const toUpdate = [];

  for (const product of products) {
    const rowKey = product.id;
    const productId = product.productId ?? product.id;
    if (productId == null || productId === "") continue;

    const serviceSafetyStock = changedStockField(
      product.serviceSafetyEnabled,
      currentServiceLevels,
      initialServiceLevels,
      rowKey
    );
    const salesSafetyStock = changedStockField(
      product.salesSafetyEnabled,
      currentSalesLevels,
      initialSalesLevels,
      rowKey
    );
    if (serviceSafetyStock === undefined && salesSafetyStock === undefined) continue;

    const hadInitial = Boolean(
      configuredProductIds[rowKey] ||
        configuredProductIds[String(rowKey)] ||
        configuredProductIds[productId] ||
        configuredProductIds[String(productId)]
    );
    const entry = { productId };
    if (serviceSafetyStock !== undefined) entry.serviceSafetyStock = serviceSafetyStock;
    if (salesSafetyStock !== undefined) entry.salesSafetyStock = salesSafetyStock;

    if (hadInitial) toUpdate.push(entry);
    else toCreate.push(entry);
  }

  const results = { created: 0, updated: 0 };

  if (toCreate.length > 0) {
    await saveLocationStockLevels(warehouseId, toCreate);
    results.created = toCreate.length;
  }

  if (toUpdate.length > 0) {
    await Promise.all(
      toUpdate.map((item) => {
        const fields = {};
        if (Object.prototype.hasOwnProperty.call(item, "serviceSafetyStock")) {
          fields.serviceSafetyStock = item.serviceSafetyStock;
        }
        if (Object.prototype.hasOwnProperty.call(item, "salesSafetyStock")) {
          fields.salesSafetyStock = item.salesSafetyStock;
        }
        return updateLocationStockLevel(warehouseId, item.productId, fields);
      })
    );
    results.updated = toUpdate.length;
  }

  return results;
}

/**
 * GET /saas/ims/location-stock-levels/search/warehouse/{warehouseId}/configured — IMS09S
 * Returns [{ productId, serviceSafetyStock, salesSafetyStock, locationStockId?, ... }] across all pages.
 */
export async function fetchConfiguredLocationStockLevels(warehouseId, params = {}) {
  const pageSize = params.size ?? 10;
  const sortBy = params.sortBy || "locationStockId";
  const sortDir = params.sortDir || "ASC";
  const items = [];
  let page = 0;
  let totalPages = 1;

  while (page < totalPages) {
    const query = buildPageQuery({ page, size: pageSize, sortBy, sortDir });
    const response = await imsGet(
      `/location-stock-levels/search/warehouse/${encodeURIComponent(warehouseId)}/configured?${query}`,
      LOCATION_STOCK_LEVELS_SEARCH
    );
    const result = unwrapPage(response, { page, size: pageSize });
    const batch = result.content || [];
    for (const row of batch) {
      if (!row || typeof row !== "object") continue;
      const productId = firstDefined(
        row.productId,
        row.product_id,
        row.product?.productId,
        row.product?.id
      );
      const serviceSafetyStock = firstDefined(
        row.serviceSafetyStock,
        row.service_safety_stock,
        row.minimumStockLevel,
        row.minimum_stock_level,
        row.minStockLevel,
        row.min_stock_level
      );
      const salesSafetyStock = firstDefined(
        row.salesSafetyStock,
        row.sales_safety_stock
      );
      if (productId == null || productId === "") continue;
      items.push({
        productId,
        serviceSafetyStock: normalizeStockLevelValue(serviceSafetyStock),
        salesSafetyStock: normalizeStockLevelValue(salesSafetyStock),
        minimumStockLevel: normalizeStockLevelValue(serviceSafetyStock),
        locationStockId: firstDefined(row.locationStockId, row.location_stock_id, row.id),
        raw: row,
      });
    }
    totalPages = Math.max(1, Number(result.totalPages) || 1);
    if (batch.length === 0) break;
    page += 1;
  }

  return items;
}

/** productId (string) → { serviceSafetyStock, salesSafetyStock } for prefilling the configure form. */
export async function fetchConfiguredStockLevelMap(warehouseId) {
  const items = await fetchConfiguredLocationStockLevels(warehouseId);
  const map = {};
  for (const item of items) {
    map[String(item.productId)] = {
      configured: true,
      serviceSafetyStock:
        item.serviceSafetyStock == null ? undefined : String(item.serviceSafetyStock),
      salesSafetyStock: item.salesSafetyStock == null ? undefined : String(item.salesSafetyStock),
    };
  }
  return map;
}
